import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FinanceService } from '../services/finance.service';
import { FinanceDashboardDTO } from '../model/finance-dashboard-dto';

@Component({
  selector: 'app-admin-finances',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatExpansionModule,
    MatTooltipModule
  ],
  templateUrl: './admin-finances.component.html',
  styleUrl: './admin-finances.component.scss'
})
export class AdminFinancesComponent implements OnInit {

  finances: FinanceDashboardDTO | null = null;
  loading: boolean = false;
  error: string | null = null;

  // ---- Filtro de rango de fechas (aplica a jobsCount, avgTicket, byCategory, byPaymentMethod) ----
  filterFrom: Date | null = null;
  filterTo: Date | null = null;

  private readonly paymentMethodIcons: { [key: string]: string } = {
    PSE: 'account_balance',
    TARJETA: 'credit_card'
  };

  private readonly paymentMethodLabels: { [key: string]: string } = {
    PSE: 'PSE',
    TARJETA: 'Tarjeta'
  };

  constructor(private financeService: FinanceService) {}

  ngOnInit(): void {
    this.loadDashboard();
  }

  // ==================== Carga de datos ====================

  loadDashboard(): void {
    this.loading = true;
    this.error = null;

    const from = this.filterFrom ? this.formatDateForApi(this.filterFrom) : undefined;
    const to = this.filterTo ? this.formatDateForApi(this.filterTo) : undefined;

    this.financeService.getDashboard(from, to).subscribe({
      next: (response) => {
        if (response.statusCode === '000' && response.data) {
          this.finances = this.sortDashboard(response.data);
        } else {
          this.error = response.message || 'Error al cargar el dashboard financiero';
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Error al cargar el dashboard financiero: ' + err.message;
        this.loading = false;
      }
    });
  }

  applyFilter(): void {
    this.loadDashboard();
  }

  clearFilter(): void {
    this.filterFrom = null;
    this.filterTo = null;
    this.loadDashboard();
  }

  hasFilter(): boolean {
    return !!this.filterFrom || !!this.filterTo;
  }

  /** Ordena categorías y subcategorías por monto cobrado descendente (defensivo ante el orden del backend) */
  private sortDashboard(data: FinanceDashboardDTO): FinanceDashboardDTO {
    const byCategory = [...(data.byCategory || [])]
      .sort((a, b) => b.collected - a.collected)
      .map(category => ({
        ...category,
        subcategories: [...(category.subcategories || [])].sort((a, b) => b.collected - a.collected)
      }));

    return { ...data, byCategory };
  }

  /** Convierte un Date del datepicker a 'yyyy-MM-dd' en hora local (evita corrimientos por UTC) */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ==================== Estados vacíos ====================

  hasFinancesData(): boolean {
    if (!this.finances) return false;
    return this.finances.totalCollected !== 0 ||
      this.finances.totalRefunded !== 0 ||
      this.finances.totalWorkbiePaid !== 0 ||
      this.finances.totalWorkbiePending !== 0 ||
      this.finances.grossProfit !== 0;
  }

  hasCategoryData(): boolean {
    return !!this.finances?.byCategory?.length;
  }

  hasPaymentMethodData(): boolean {
    return !!this.finances?.byPaymentMethod?.length;
  }

  // ==================== Formateo ====================

  isCurrentMonth(month: string): boolean {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return month === currentMonth;
  }

  formatMonthLabel(month: string): string {
    // month viene como 'yyyy-MM'
    const [year, monthNumber] = month.split('-').map(Number);
    if (!year || !monthNumber) return month;
    const date = new Date(year, monthNumber - 1, 1);
    const label = date.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  paymentMethodIcon(method: string): string {
    return this.paymentMethodIcons[method?.toUpperCase()] || 'payment';
  }

  paymentMethodLabel(method: string): string {
    return this.paymentMethodLabels[method?.toUpperCase()] || method;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  }
}
